import { Typography } from '@packages/shared';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ServiceLayer } from '@packages/cms-components';
import { useConfig } from '@packages/utilities';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { TextWithDialog } from '../TextWithDialog';
import { taxHintMessages } from './messages';
import { useLayerTracking } from '../Tracking/hooks/useLayerTracking';
import { getTaxHintMessage } from './getTaxHintMessage';

const TaxHintFragment = graphql(/* GraphQL */ `
  fragment TaxHintFragment on Delivery {
    deliverySize
  }
`);

export type TaxHintProps = {
  /**
   * The used delivery type for a given product. Each delivery type will show its own delivery info message.
   * */
  data: FragmentType<typeof TaxHintFragment>;
};

/**
 * <p>
 * The <code>TaxHint</code> component displays a delivery info message.
 * </p>
 * */
export const TaxHint = ({ data }: TaxHintProps) => {
  const { deliverySize } = getFragmentData(TaxHintFragment, data);
  const { formatMessage } = useIntl();
  const { trackViewLayer } = useLayerTracking();
  const {
    product: {
      taxHint: { hideAdditionalDeliveryInfo, modalContent },
    },
  } = useConfig();

  const dialogContent =
    deliverySize in modalContent ? (
      <ServiceLayer serviceLayerId={modalContent[deliverySize].link} />
    ) : (
      <>{formatMessage(taxHintMessages.dialogContent)}</>
    );

  const title = formatMessage(getTaxHintMessage('TITLE', deliverySize));

  const handleClick = () => {
    trackViewLayer(`${title} (Liefertyp: ${deliverySize})`);
  };

  const messageValue = {
    link: (msg: ReactNode[]) =>
      !hideAdditionalDeliveryInfo && (
        <TextWithDialog
          message={msg}
          dialogTitle={title}
          dialogContent={dialogContent}
          onClickHandler={() => handleClick()}
          variant="body3"
          sx={{ color: 'text.secondary' }}
          displayTrackingProps={{ category: 'dv-service-layer-tax-hint' }}
        />
      ),
  };

  return (
    <Typography variant="body3" component="p" sx={{ color: 'text.secondary' }}>
      {formatMessage(getTaxHintMessage('TEXT', deliverySize), messageValue)}
    </Typography>
  );
};
